<template>
  <div class="guide-overlay" @click.self="closeGuide">
    <!-- Элемент подсветки -->
    <div class="highlight-element" :style="highlightStyle" v-if="showHighlight"></div>



    <div class="guide-container" ref="guideContainer" :style="containerStyle">
      <div class="arrow-wrapper" :style="arrowStyle">
        <div class="guide-arrow"></div>
      </div>

      <!-- Верхняя панель с индикатором и кнопкой завершения -->
      <div class="guide-header">
        <span class="page-indicator">{{ currentPage }}/{{ totalPages }}</span>
        <button class="close-button" @click="closeGuide">
          Завершить
        </button>
      </div>

      <!-- Контент гида с анимацией перехода -->
      <transition :name="transitionName" mode="out-in">
        <div class="guide-content" :key="currentPage">
          <div class="guide-page">
            <h4>{{ currentTitle }}</h4>
            <div class="guide-instruction">
              <p v-for="(item, index) in currentInstructions" :key="index">{{ item }}</p>
            </div>
          </div>
        </div>
      </transition>

      <!-- Навигационные кнопки -->
      <div class="guide-navigation">
        <button class="nav-button" @click="prevPage" :disabled="currentPage === 1">
          Назад
        </button>
        <button class="nav-button" @click="nextPage">
          {{ currentPage === totalPages ? 'Готово' : 'Далее' }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

export default {
  props: {
    pages: {
      type: Array,
      default: () => []
    },
    totalPages: {
      type: Number,
      default: 0
    }
  },
  emits: ['close', 'update-page'],
  setup(props, { emit }) {
    const currentPage = ref(1)
    const totalPages = computed(() => props.totalPages || props.pages.length)
    const transitionName = ref('slide-next')
    const guideContainer = ref(null)
    const showHighlight = ref(false)
    const highlightStyle = ref({})
    const currentHighlightCleanup = ref(null)

    const positions = ref({
      top: 0,
      left: 0,
      arrowOffset: 0,
      arrowDirection: 'down'
    })

    // Обновляем подсветку при смене страницы
    watch(currentPage, () => {
      if (currentHighlightCleanup.value) {
        currentHighlightCleanup.value()
        currentHighlightCleanup.value = null
      }
      updateHighlight()
    })

    const updateHighlight = () => {
      if (!props.pages.length) {
        showHighlight.value = false
        return
      }
      
      const currentPageData = props.pages[currentPage.value - 1]
      if (!currentPageData || !currentPageData.highlight) {
        showHighlight.value = false
        return
      }

      const element = document.querySelector(currentPageData.highlight)
      if (!element) {
        showHighlight.value = false
        return
      }

      const updateHighlightPosition = () => {
        const rect = element.getBoundingClientRect()
        // Увеличиваем область подсветки на 5px с каждой стороны
        const padding = 5
        const highlightWidth = rect.width + padding * 2
        const highlightHeight = rect.height + padding * 2

        highlightStyle.value = {
          top: `${rect.top - padding}px`,
          left: `${rect.left - padding}px`,
          width: `${highlightWidth}px`,
          height: `${highlightHeight}px`,
          boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.7)`, // Более темный оверлей
          border: `2px solid #4a90e2`, // Яркая синяя рамка
          borderRadius: element.classList.contains('faq-container') ||
            element.classList.contains('avatar-container')
            ? '50%' : '4px',
          zIndex: '100002',
          pointerEvents: 'none',
          transition: 'all 0.3s ease'
        }
      }

      // Обновляем позицию сразу
      updateHighlightPosition()

      // Создаем наблюдатель за изменениями элемента
      const observer = new MutationObserver(updateHighlightPosition)
      observer.observe(element, {
        attributes: true,
        childList: true,
        subtree: true
      })

      // Обновляем позицию при скролле
      const scrollHandler = () => {
        updateHighlightPosition()
      }

      window.addEventListener('scroll', scrollHandler, true)

      // Сохраняем ссылки для очистки
      currentHighlightCleanup.value = () => {
        observer.disconnect()
        window.removeEventListener('scroll', scrollHandler, true)
      }

      showHighlight.value = true
    }


    const currentTitle = computed(() => {
      if (!props.pages.length) return ''
      return props.pages[currentPage.value - 1]?.title || ''
    })

    const currentInstructions = computed(() => {
      if (!props.pages.length) return []
      return props.pages[currentPage.value - 1]?.instructions || []
    })

    const calculatePosition = () => {
      if (!props.pages.length) return
    
      const currentPageData = props.pages[currentPage.value - 1]
      if (!currentPageData || !currentPageData.target) return
      const target = document.querySelector(currentPageData.target)
      if (!target || !guideContainer.value) return

      const targetRect = target.getBoundingClientRect()
      const containerRect = guideContainer.value.getBoundingClientRect()
      const scrollY = window.scrollY || window.pageYOffset
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let containerLeft, containerTop, arrowOffset, arrowDirection

      // Получаем настройки стрелки из конфигурации страницы
      const pageConfig = props.pages[currentPage.value - 1]
      const preferredArrowDirection = pageConfig.arrowDirection || 'down'

      // Общая логика для кнопок в utils-block (страницы 10-12)
      if (currentPage.value >= 10 && currentPage.value <= 12) {
        if (window.innerWidth >= 768) {
          // Общие настройки
          arrowDirection = 'down'
          arrowOffset = 0

          // Индивидуальные настройки для каждой кнопки
          if (currentPage.value === 10) { // Сохранить
            containerTop = targetRect.bottom + scrollY + 50 // Ниже
            containerLeft = targetRect.right - containerRect.width + 20 // Правее
            arrowOffset = containerRect.width  // Стрелка левее
          }
          else if (currentPage.value === 11) { // Скачать
            containerTop = targetRect.bottom + scrollY + 30 // Чуть ниже
            containerLeft = targetRect.left + targetRect.width / 2 - containerRect.width / 2
          }
          else if (currentPage.value === 12) { // Удалить
            containerTop = targetRect.bottom + scrollY + 30 // Чуть выше
            containerLeft = targetRect.left + targetRect.width / 2 - containerRect.width / 2
          }

          // Проверка границ и корректировка
          if (containerTop + containerRect.height > viewportHeight - 20) {
            containerTop = targetRect.top + scrollY - containerRect.height - 15
            arrowDirection = 'up'

            // Дополнительные корректировки для верхнего положения
            if (currentPage.value === 10) containerTop -= 5
            if (currentPage.value === 12) containerTop += 5
          }

          // Гарантируем, что не выходим за границы экрана
          containerLeft = Math.max(20, Math.min(containerLeft, viewportWidth - containerRect.width - 20))
          containerTop = Math.max(20, Math.min(containerTop, viewportHeight - containerRect.height - 20))

        } else {
          // Mobile version
          containerLeft = 20
          containerTop = targetRect.bottom + scrollY + 15
          arrowDirection = 'down'
          arrowOffset = 0
          guideContainer.value.style.maxWidth = 'calc(100% - 40px)'
        }
      }
      // Логика для "Мои проекты" (страница 13)
      else if (currentPage.value === 13) {
        if (window.innerWidth >= 768) {
          // Desktop - позиционируем под пунктом навигации
          containerTop = targetRect.bottom + scrollY + 15
          containerLeft = targetRect.left + targetRect.width / 2 - containerRect.width / 2
          arrowDirection = 'down'
          arrowOffset = 0

          containerLeft = Math.max(20, Math.min(containerLeft, viewportWidth - containerRect.width - 20))
        } else {
          // Mobile - ищем в выпадающем меню
          const mobileTarget = document.querySelector('.menu-section a[href*="projects"]') || target
          const mobileRect = mobileTarget.getBoundingClientRect()
          containerLeft = 20
          containerTop = mobileRect.bottom + scrollY + 15
          arrowDirection = 'down'
          arrowOffset = 0
        }
      }
      // Остальные случаи (страницы 1-9)
      else {
        switch (currentPage.value) {
          case 1:
            containerTop = targetRect.bottom + scrollY + 22
            containerLeft = targetRect.left + targetRect.width - containerRect.width + 40
            arrowDirection = 'down'
            arrowOffset = containerRect.width - 210
            break

          case 2:
            containerTop = targetRect.bottom + scrollY + 22
            containerLeft = targetRect.left + targetRect.width / 2 - containerRect.width / 2
            arrowDirection = 'down'
            arrowOffset = containerRect.width - 200
            break

          case 3:
          case 4:
            containerTop = targetRect.bottom + scrollY + 15
            containerLeft = targetRect.left + targetRect.width / 2 - containerRect.width / 2
            arrowDirection = 'down'
            arrowOffset = 0
            break

          case 5:
          case 6:
          case 7:
          case 8:
            if (window.innerWidth >= 768) {
              containerLeft = targetRect.right + 20
              containerTop = targetRect.top + scrollY + targetRect.height / 2 - containerRect.height / 2
              arrowOffset = -20
              arrowDirection = 'left'

              if (containerLeft + containerRect.width > viewportWidth - 10) {
                containerLeft = targetRect.left - containerRect.width - 20
                arrowDirection = 'right'
                arrowOffset = containerRect.width + 15
              }
            } else {
              containerLeft = 10
              containerTop = targetRect.bottom + scrollY + 10
              arrowDirection = 'down'
              arrowOffset = 0
            }
            break

          case 9:
            if (window.innerWidth >= 768) {
              containerLeft = targetRect.right + 20
              containerTop = targetRect.top + scrollY + targetRect.height / 2 - containerRect.height / 2
              arrowDirection = 'left'
              arrowOffset = -20

              if (containerLeft + containerRect.width > viewportWidth - 10) {
                containerLeft = targetRect.left - containerRect.width - 20
                arrowDirection = 'right'
                arrowOffset = containerRect.width + 15
              }
            } else {
              containerLeft = 10
              containerTop = targetRect.bottom + scrollY + 10
              arrowDirection = 'down'
              arrowOffset = 0
            }
            break

          default:
            containerTop = targetRect.bottom + scrollY + 15
            containerLeft = targetRect.left + targetRect.width / 2 - containerRect.width / 2
            arrowDirection = preferredArrowDirection
            arrowOffset = 0
        }
      }

      // Финальная корректировка позиции
      containerLeft = Math.max(10, Math.min(containerLeft, viewportWidth - containerRect.width - 10))
      containerTop = Math.max(10, Math.min(containerTop, viewportHeight - containerRect.height - 10))

      positions.value = {
        top: containerTop,
        left: containerLeft,
        arrowOffset,
        arrowDirection
      }

      emit('update-page', currentPage.value)
    }

    const containerStyle = computed(() => ({
      top: `${positions.value.top}px`,
      left: `${positions.value.left}px`
    }))

    const arrowStyle = computed(() => {
      if (positions.value.arrowDirection === 'left') {
        return {
          left: '-15px',
          top: '50%',
          transform: 'translateY(-50%) rotate(90deg)'
        }
      }
      if (positions.value.arrowDirection === 'right') {
        return {
          right: '-15px',
          top: '50%',
          transform: 'translateY(-50%) rotate(-90deg)'
        }
      }
      if (positions.value.arrowDirection === 'up') {
        return {
          left: '50%',
          bottom: '-15px',
          transform: 'translateX(-50%) rotate(180deg)'
        }
      }
      return {
        left: `calc(50% + ${positions.value.arrowOffset}px)`,
        top: '-15px',
        transform: 'none'
      }
    })

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        transitionName.value = 'slide-next'
        currentPage.value += 1
        calculatePosition()
      } else {
        closeGuide()
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        transitionName.value = 'slide-prev'
        currentPage.value -= 1
        calculatePosition()
      }
    }

    const closeGuide = () => {
      emit('close')
    }

    onMounted(() => {
      calculatePosition()
      updateHighlight()
      window.addEventListener('resize', calculatePosition)
      window.addEventListener('scroll', calculatePosition, true)
    })

    onUnmounted(() => {
      if (currentHighlightCleanup.value) {
        currentHighlightCleanup.value()
      }
      window.removeEventListener('resize', calculatePosition)
      window.removeEventListener('scroll', calculatePosition, true)
    })

    return {
      currentPage,
      currentTitle,
      currentInstructions,
      containerStyle,
      arrowStyle,
      transitionName,
      guideContainer,
      nextPage,
      prevPage,
      closeGuide,
      showHighlight,
      highlightStyle
    }
  }
}
</script>

<style scoped>
.guide-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100000;
}

.highlight-element {
  position: fixed;
  pointer-events: none;
  z-index: 100002;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
  border: 2px solid #4a90e2;
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    border-color: #4a90e2;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
  }

  50% {
    border-color: #6aa8ff;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  }

  100% {
    border-color: #4a90e2;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
  }
}


.guide-container {
  position: absolute;
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 300px;
  max-width: calc(100vw - 40px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 100005;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.guide-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.page-indicator {
  font-size: 14px;
  color: #718096;
  font-weight: bold;
}

.close-button {
  background: none;
  border: none;
  color: #4a90e2;
  font-size: 14px;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  font-weight: 600;
}

.close-button:hover {
  background-color: #f0f7ff;
}

.arrow-wrapper {
  position: absolute;
  width: 20px;
  height: 20px;
  z-index: 100006;
  pointer-events: none;
  transition: all 0.3s ease;
}

.guide-arrow {
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 15px solid white;
  position: absolute;
  filter: drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1));
}

.guide-content {
  position: relative;
  min-height: 120px;
}

.guide-page h4 {
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 18px;
}

.guide-instruction {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
}

.guide-instruction p {
  margin: 8px 0;
}

.guide-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.nav-button {
  background: #4a90e2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.nav-button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

.nav-button:hover:not(:disabled) {
  background: #357abd;
}

/* Анимации */
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: all 0.3s ease;
}

.slide-next-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-next-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-prev-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.slide-prev-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

@media (max-width: 768px) {
  .guide-container {
    width: 90%;
    left: 50% !important;
    transform: translateX(-50%) !important;
  }

  .arrow-wrapper {
    display: none;
  }

  .guide-content {
    max-height: 60vh;
    overflow-y: auto;
  }

  .highlight-element {
    display: none;
  }
}
</style>